import React, { useEffect } from 'react'
import { twMerge } from 'tailwind-merge'
import { RoundIconComponent } from '../../round-icon-component'
import { ModalWindowBody } from '../components/modal-window-body'
import { ModalWindowContainer } from '../components/modal-window-container'
import { ModalWindowFooter } from '../components/modal-window-footer'
import { ModalWindowHeader } from '../components/modal-window-header'
import { CommonModalWindowProps, ModalWindowSizeEnum } from '../types'

export enum DialogWindowIconThemeEnum {
  Confirmation = 'confirmation',
  Warning = 'warning',
}

export interface DialogWindowProps
  extends Omit<
    CommonModalWindowProps,
    'hideHeader' | 'startElement' | 'children' | 'hideHorizontalBorder' | 'size'
  > {
  description?: string
  icon?: React.ReactNode
  iconTheme?: DialogWindowIconThemeEnum
  children?: React.ReactNode
}

export const DialogWindow = ({
  isOpened = false,
  children,
  onClose,
  width = 500,
  title,
  description,
  cancelButtonProps,
  confirmButtonProps,
  icon,
  iconTheme = DialogWindowIconThemeEnum.Confirmation,
}: DialogWindowProps) => {
  const size = ModalWindowSizeEnum.Medium

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        e.preventDefault()
        cancelButtonProps?.onClick?.()
        onClose()
      }
    }
    document.addEventListener('keydown', handleGlobalKeyDown)
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [onClose, cancelButtonProps?.onClick])

  return (
    <ModalWindowContainer isOpened={isOpened} onClose={onClose} width={width}>
      <ModalWindowHeader
        size={size}
        onClose={onClose}
        hideHorizontalBorder
        startElement={
          <RoundIconComponent
            className={twMerge(
              'shrink-0',
              iconTheme === DialogWindowIconThemeEnum.Warning &&
                'bg-bgError [&>div>svg]:stroke-iconError',
              iconTheme === DialogWindowIconThemeEnum.Confirmation &&
                'bg-primary-100 [&>div>svg]:stroke-iconPrimary',
            )}
          >
            {icon}
          </RoundIconComponent>
        }
      />
      <ModalWindowBody size={size} className="pt-0 pb-3">
        <div className="flex flex-col gap-3">
          {title && <span className="text-heading-3">{title}</span>}
          {description && (
            <span className="text-M text-textTertiary">{description}</span>
          )}
          {children}
        </div>
      </ModalWindowBody>
      <ModalWindowFooter
        size={size}
        hideHorizontalBorder
        onClose={onClose}
        confirmButtonProps={confirmButtonProps}
        cancelButtonProps={cancelButtonProps}
        confirmOnEnter
      />
    </ModalWindowContainer>
  )
}
